import type { Adjacency } from './types';
import { triArea, triNormal } from './triangle';

/** Per-triangle unit face normals (3 per triangle). */
export function computeFaceNormals(positions: Float32Array, indices: Uint32Array): Float32Array {
  const triCount = indices.length / 3;
  const out = new Float32Array(triCount * 3);
  for (let t = 0; t < triCount; t++) {
    const n = triNormal(positions, indices, t);
    out[t * 3] = n[0];
    out[t * 3 + 1] = n[1];
    out[t * 3 + 2] = n[2];
  }
  return out;
}

/** Per-triangle world areas. */
export function computeFaceAreas(positions: Float32Array, indices: Uint32Array): Float32Array {
  const triCount = indices.length / 3;
  const out = new Float32Array(triCount);
  for (let t = 0; t < triCount; t++) out[t] = triArea(positions, indices, t);
  return out;
}

/**
 * Area-weighted neighbour-average smoothing of face normals — the noise-reduction
 * knob. Each pass replaces a normal with the normalized area-weighted average of
 * itself + its ≤3 edge neighbours. Used for region-grow acceptance ONLY; never
 * written back to the rendered mesh.
 */
export function smoothFaceNormals(
  normals: Float32Array,
  areas: Float32Array,
  adj: Adjacency,
  iterations = 1,
): Float32Array {
  const triCount = normals.length / 3;
  let current = Float32Array.from(normals);
  for (let it = 0; it < iterations; it++) {
    const next = new Float32Array(triCount * 3);
    for (let t = 0; t < triCount; t++) {
      const cx = current[t * 3];
      const cy = current[t * 3 + 1];
      const cz = current[t * 3 + 2];
      let nx = cx * areas[t];
      let ny = cy * areas[t];
      let nz = cz * areas[t];
      for (let s = 0; s < 3; s++) {
        const nb = adj.adjacent[t * 3 + s];
        if (nb >= 0) {
          // Sign-align each neighbour to the center normal before accumulating: the
          // grow acceptance is deliberately winding-tolerant (abs-dot), but plain
          // averaging is NOT — a mixed-winding seam (e.g. a mirrored GLB node baked
          // with a negative-determinant world matrix) has antiparallel neighbours
          // that cancel here, leaving noise-dominated garbage normals at the seam.
          const d = current[nb * 3] * cx + current[nb * 3 + 1] * cy + current[nb * 3 + 2] * cz;
          const w = d < 0 ? -areas[nb] : areas[nb];
          nx += current[nb * 3] * w;
          ny += current[nb * 3 + 1] * w;
          nz += current[nb * 3 + 2] * w;
        }
      }
      const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
      if (len > 1e-12) {
        next[t * 3] = nx / len;
        next[t * 3 + 1] = ny / len;
        next[t * 3 + 2] = nz / len;
      } else {
        next[t * 3] = current[t * 3];
        next[t * 3 + 1] = current[t * 3 + 1];
        next[t * 3 + 2] = current[t * 3 + 2];
      }
    }
    current = next;
  }
  return current;
}
